// Reference tables for the UDS (ISO 14229) Diagnostic Session Simulator.
// Kept separate from the ECU engine/decoder logic so new services, NRCs, or DIDs
// can be added here without touching the state machine or byte-decoding code.

export const SESSIONS = {
  0x01: { id: 0x01, key: 'default', name: 'Default Session' },
  0x02: { id: 0x02, key: 'programming', name: 'Programming Session' },
  0x03: { id: 0x03, key: 'extended', name: 'Extended Diagnostic Session' },
};

export const SESSION_KEY_TO_ID = { default: 0x01, programming: 0x02, extended: 0x03 };

export const SESSION_NAME_BY_KEY = Object.fromEntries(Object.values(SESSIONS).map((s) => [s.key, s.name]));

// Services implemented by this simulator. `name` and `short` feed the guided-mode
// dropdown and the reference panel; `hasSubFunction` controls whether the guided
// form renders a sub-function selector before service-specific parameters.
export const SERVICES = [
  { sid: 0x10, name: 'Diagnostic Session Control', short: 'Switch between Default, Programming, and Extended sessions.' },
  { sid: 0x11, name: 'ECU Reset', short: 'Simulate a hard or soft reset of the ECU.' },
  { sid: 0x14, name: 'Clear Diagnostic Information', short: 'Erase stored DTCs from fault memory.' },
  { sid: 0x19, name: 'Read DTC Information', short: 'Read stored DTCs and their status, or a fault snapshot.' },
  { sid: 0x22, name: 'Read Data By Identifier', short: 'Read a data value (VIN, part number, etc.) by its 2-byte DID.' },
  { sid: 0x27, name: 'Security Access', short: 'Seed/key exchange to unlock write and erase services.' },
  { sid: 0x2E, name: 'Write Data By Identifier', short: 'Write a data value by its 2-byte DID (requires security unlock).' },
  { sid: 0x3E, name: 'Tester Present', short: 'Keep a non-default session alive; resets the S3 session timer.' },
];

export const SERVICE_BY_SID = Object.fromEntries(SERVICES.map((s) => [s.sid, s]));

export const serviceName = (sid) => SERVICE_BY_SID[sid]?.name || `Unknown Service (0x${hex2(sid)})`;

// Negative Response Codes. Every code the simulator can return must have an entry
// here — decoder.js looks codes up by number, and the reference panel renders this
// whole table, so it is the single source of truth for NRC text.
export const NRC_TABLE = {
  0x11: { name: 'serviceNotSupported', meaning: 'The ECU does not implement this Service ID at all.' },
  0x12: { name: 'subFunctionNotSupported', meaning: 'The service is known, but this particular sub-function is not implemented.' },
  0x13: { name: 'incorrectMessageLengthOrInvalidFormat', meaning: 'The request has the wrong number of bytes for this service/sub-function.' },
  0x22: { name: 'conditionsNotCorrect', meaning: 'The ECU understood the request but its current state (e.g. wrong diagnostic session) does not allow it right now.' },
  0x24: { name: 'requestSequenceError', meaning: 'This request is out of order — a required prior step (e.g. requesting a seed before sending a key) was skipped.' },
  0x31: { name: 'requestOutOfRange', meaning: 'A parameter in the request (DID, DTC, sub-function value) is not supported by this ECU.' },
  0x33: { name: 'securityAccessDenied', meaning: 'This operation requires an unlocked security level, and the session is currently locked.' },
  0x35: { name: 'invalidKey', meaning: 'The key sent does not match the key computed from the seed for this security level.' },
  0x36: { name: 'exceedNumberOfAttempts', meaning: 'Too many invalid keys were sent in a row — the ECU has locked out further Security Access attempts for a delay period.' },
  0x37: { name: 'requiredTimeDelayNotExpired', meaning: 'A Security Access lockout delay is still active; wait for it to expire before trying again.' },
  0x78: { name: 'requestCorrectlyReceived-ResponsePending', meaning: 'The ECU accepted the request but needs more time to prepare the actual response.' },
};

export const nrcName = (code) => NRC_TABLE[code]?.name || 'unknown';
export const nrcMeaning = (code) => NRC_TABLE[code]?.meaning || 'This negative response code is not in the simulator’s reference table.';

// Common Data Identifiers, pre-populated on every default ECU profile. Author
// mode profiles may add more; `computed` DIDs are derived live from ECU state
// rather than stored as a static value (see engine.js `readDid`).
export const COMMON_DIDS = [
  { id: 0xF190, name: 'VIN', format: 'ascii', length: 17, writable: false, defaultValue: 'WBA3A5C51DF812345' },
  { id: 0xF187, name: 'Manufacturer Spare Part Number', format: 'ascii', length: 10, writable: true, defaultValue: 'ATX0102030' },
  { id: 0xF189, name: 'ECU Software Version', format: 'ascii', length: 8, writable: false, defaultValue: '1.04.002' },
  { id: 0xF18C, name: 'ECU Serial Number', format: 'ascii', length: 12, writable: false, defaultValue: 'SN00A1B2C3D4' },
  { id: 0xF191, name: 'ECU Hardware Number', format: 'ascii', length: 8, writable: false, defaultValue: 'HW-3021A' },
  { id: 0xF186, name: 'Active Diagnostic Session', format: 'numeric', length: 1, writable: false, computed: 'activeSession' },
];

export const COMMON_DID_BY_ID = Object.fromEntries(COMMON_DIDS.map((d) => [d.id, d]));

// DTC status mask bits (ISO 14229-1). Shared with the DTC & Status Byte Decoder —
// the single definition lives in lib/dtcDecoder/constants.js.
export { DTC_STATUS_BITS } from '../dtcDecoder/constants';

export const hex2 = (n) => n.toString(16).toUpperCase().padStart(2, '0');
export const hex4 = (n) => n.toString(16).toUpperCase().padStart(4, '0');

export const bytesToHexString = (bytes) => bytes.map(hex2).join(' ');

export const DEFAULT_SECURITY_ALGORITHM = { type: 'xor', value: 0x2571 };

export const DEFAULT_TIMING = {
  s3Ms: 20000, // simulator uses a longer-than-real S3 timeout (spec default 5000ms) so users have time to read the log
  securityMaxAttempts: 3,
  securityLockoutMs: 30000,
};
